import React, { createContext, useCallback, useContext, useState } from 'react';
import type { InsuredResult } from '../features/chat/InsuredMentionDropdown';

/**
 * Which Digital Workforce agent the app-wide chat bar (GlobalChatBar) is
 * currently talking to. Defaults to the primary agent when nothing here has
 * been set — see GlobalChatBar. Setting this from anywhere (the Digital
 * Team sidebar strip, an agent's "Start Chat" button) is what makes the
 * bar's avatar/name follow you around instead of staying fixed.
 *
 * Also carries a small "who is replying to you right now" signal: any chat
 * surface streaming an agent's reply (the docked bar, the full chat page)
 * marks that agent busy for the duration, and the time it finished is kept
 * so the Digital Team strip can show "Replying to you…" / "Chatted with you
 * · 3m ago" alongside commands and routines.
 *
 * And a "chat target": a page that has its own conversation (a command open
 * in Command Center) can claim the docked bar as its composer. While a
 * target is set the bar keeps its look but posts there instead of opening
 * its own quick-chat panel. `dockReady` says whether the docked bar is
 * actually on screen, so the page only hides its own composer when there is
 * a bar to replace it.
 */
export interface ChatTarget {
  /** Short label shown in the bar, e.g. "#1234". */
  label: string;
  /** Post a message (files are uploaded through uploadFiles first).
   *  `mentions` maps each @-linked display name in `text` to its record. */
  submit: (text: string, mentions: Map<string, InsuredResult>) => void;
  /** Optional "Select from AMS" in the bar's attach menu. */
  openAmsPicker?: () => void;
  /** Upload files to the target (they show in the target's own UI). */
  uploadFiles: (files: File[]) => void;
  /** True while the target is saving or the agent is replying. */
  busy: boolean;
  /** The target already holds attachments, so sending with no text is OK. */
  hasAttachments: boolean;
  /** The target's attachments, shown in the bar's file tray. */
  files: Array<{ key: string; name: string; size?: number; status: 'uploading' | 'ready' | 'error'; error?: string }>;
  removeFile: (key: string) => void;
}
export interface ActiveAgentInfo {
  id: string;
  name: string;
  avatarColor: string;
  avatarInitials: string;
  avatarPhoto?: string;
}

interface ActiveAgentContextValue {
  activeAgent: ActiveAgentInfo | null;
  setActiveAgent: (agent: ActiveAgentInfo) => void;
  /** Bumped whenever something wants the bar to pop open, not just re-target. */
  openSignal: number;
  requestOpen: (agent?: ActiveAgentInfo) => void;
  /** Agent id -> number of chat surfaces currently streaming its reply. */
  replyingAgents: Record<string, number>;
  /** Agent id -> when its most recent reply to you finished (ms epoch). */
  lastChatAt: Record<string, number>;
  /** Mark an agent as replying; returns the matching "done" call. */
  beginAgentReply: (agentId: string) => () => void;
  chatTarget: ChatTarget | null;
  setChatTarget: (target: ChatTarget | null) => void;
  dockReady: boolean;
  setDockReady: (ready: boolean) => void;
}

const ActiveAgentContext = createContext<ActiveAgentContextValue | undefined>(undefined);

export const ActiveAgentProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [activeAgent, setActiveAgentState] = useState<ActiveAgentInfo | null>(null);
  const [openSignal, setOpenSignal] = useState(0);
  const [replyingAgents, setReplyingAgents] = useState<Record<string, number>>({});
  const [lastChatAt, setLastChatAt] = useState<Record<string, number>>({});
  const [chatTarget, setChatTarget] = useState<ChatTarget | null>(null);
  const [dockReady, setDockReady] = useState(false);

  const setActiveAgent = useCallback((agent: ActiveAgentInfo) => {
    setActiveAgentState(agent);
  }, []);

  const requestOpen = useCallback((agent?: ActiveAgentInfo) => {
    if (agent) setActiveAgentState(agent);
    setOpenSignal((n) => n + 1);
  }, []);

  const beginAgentReply = useCallback((agentId: string) => {
    setReplyingAgents((prev) => ({ ...prev, [agentId]: (prev[agentId] ?? 0) + 1 }));
    let done = false;
    return () => {
      if (done) return;
      done = true;
      setReplyingAgents((prev) => {
        const n = (prev[agentId] ?? 1) - 1;
        const next = { ...prev };
        if (n > 0) next[agentId] = n; else delete next[agentId];
        return next;
      });
      setLastChatAt((prev) => ({ ...prev, [agentId]: Date.now() }));
    };
  }, []);

  return (
    <ActiveAgentContext.Provider value={{
      activeAgent, setActiveAgent, openSignal, requestOpen,
      replyingAgents, lastChatAt, beginAgentReply,
      chatTarget, setChatTarget, dockReady, setDockReady,
    }}>
      {children}
    </ActiveAgentContext.Provider>
  );
};

export function useActiveAgent(): ActiveAgentContextValue {
  const ctx = useContext(ActiveAgentContext);
  if (!ctx) throw new Error('useActiveAgent must be used within an ActiveAgentProvider');
  return ctx;
}

/** Marks `agentId` as replying to the user while `active` is true. */
export function useAgentReplySignal(agentId: string | null | undefined, active: boolean): void {
  const { beginAgentReply } = useActiveAgent();
  React.useEffect(() => {
    if (!agentId || !active) return;
    return beginAgentReply(agentId);
  }, [agentId, active, beginAgentReply]);
}
